import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Lock, 
  AlertTriangle,
  ChevronRight,
  Settings,
  Users,
  Wifi,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  Save,
  RefreshCw,
  CheckCircle,
  XCircle,
  X,
  Clock,
  Smartphone,
  Globe,
  Server,
  Activity,
  Search,
  Filter
} from 'lucide-react';
import { formatDate } from '../../services/api';
import { ipWhitelistAPI } from '../../services/api.js';

const SecurityManagement = () => {
  const [activeTab, setActiveTab] = useState('access-control');
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  // IP Whitelist State
  const [ipWhitelist, setIpWhitelist] = useState([]);
  const [newIp, setNewIp] = useState({ ip: '', description: '', enabled: true });

  // Load IP whitelist on component mount
  useEffect(() => {
    const loadIPWhitelist = async () => {
      try {
        const response = await ipWhitelistAPI.getIPWhitelist();
        if (response.data.success) {
          setIpWhitelist(response.data.data);
        }
      } catch (error) {
        console.error('Failed to load IP whitelist:', error);
      }
    };

    loadIPWhitelist();
  }, []);



  // MFA Settings State
  const [mfaSettings, setMfaSettings] = useState({
    enforcedForAll: false,
    enforcedForAdmins: true,
    allowedMethods: ['authenticator', 'sms', 'email'],
    backupCodes: true,
    sessionTimeout: 60 // minutes
  });

  // Demo Active Sessions based on Demo Accounts
  const [activeSessions, setActiveSessions] = useState([
    {
      id: 1,
      user: 'admin@demo.com',
      role: 'Super Admin',
      displayName: 'Demo Super Admin',
      ipAddress: '192.168.1.100',
      location: 'Kuala Lumpur, Malaysia',
      loginTime: '2025-01-14T09:30:00Z',
      lastActivity: '2025-01-14T10:25:00Z',
      device: 'Chrome on Windows 11',
      status: 'active',
      sessionDuration: '0:55:00'
    },
    {
      id: 2,
      user: 'fa@demo.com',
      role: 'Facility Agent',
      displayName: 'Demo Facility Agent Corporation',
      ipAddress: '203.115.45.67',
      location: 'Petaling Jaya, Malaysia',
      loginTime: '2025-01-14T08:15:00Z',
      lastActivity: '2025-01-14T10:20:00Z',
      device: 'Safari on macOS',
      status: 'active',
      sessionDuration: '2:05:00'
    },
    {
      id: 3,
      user: 'issuer@demo.com',
      role: 'Issuer',
      displayName: 'Demo Issuer Corporation',
      ipAddress: '125.23.45.67',
      location: 'Kuala Lumpur, Malaysia',
      loginTime: '2025-01-14T07:45:00Z',
      lastActivity: '2025-01-14T09:50:00Z',
      device: 'Edge on Windows 11',
      status: 'idle',
      sessionDuration: '2:35:00'
    },
    {
      id: 4,
      user: 'custodian@demo.com',
      role: 'Custodian',
      displayName: 'Demo Custodian Corporation',
      ipAddress: '192.168.1.105',
      location: 'Kuala Lumpur, Malaysia',
      loginTime: '2025-01-14T08:45:00Z',
      lastActivity: '2025-01-14T10:15:00Z',
      device: 'Firefox on Windows 11',
      status: 'active',
      sessionDuration: '1:30:00'
    },
    {
      id: 5,
      user: 'arranger@demo.com',
      role: 'Arranger',
      displayName: 'Demo Arranger Corporation',
      ipAddress: '172.16.0.45',
      location: 'Shah Alam, Malaysia',
      loginTime: '2025-01-14T09:00:00Z',
      lastActivity: '2025-01-14T10:00:00Z',
      device: 'Chrome on macOS',
      status: 'active',
      sessionDuration: '1:25:00'
    },
    {
      id: 6,
      user: 'exchange@demo.com',
      role: 'Exchange',
      displayName: 'Demo Exchange Corporation',
      ipAddress: '10.0.0.88',
      location: 'Cyberjaya, Malaysia',
      loginTime: '2025-01-14T08:00:00Z',
      lastActivity: '2025-01-14T09:45:00Z',
      device: 'Chrome on Ubuntu',
      status: 'idle',
      sessionDuration: '2:25:00'
    },
    {
      id: 7,
      user: 'investor@demo.com',
      role: 'Investor',
      displayName: 'Demo Investor',
      ipAddress: '203.145.67.89',
      location: 'Johor Bahru, Malaysia',
      loginTime: '2025-01-14T09:15:00Z',
      lastActivity: '2025-01-14T10:10:00Z',
      device: 'Safari on iPhone',
      status: 'active',
      sessionDuration: '1:10:00'
    }
  ]);

  // Mock Security Events
  const [securityEvents] = useState([
    {
      id: 1,
      type: 'Failed Login',
      user: 'unknown@suspicious.com',
      ipAddress: '45.67.89.12',
      timestamp: '2025-07-18T13:41:12.000Z',
      severity: 'high',
      description: 'Multiple failed login attempts',
      blocked: true
    },
    {
      id: 2,
      type: 'Unusual Location',
      user: 'user@issuer.com',
      ipAddress: '78.90.12.34',
      timestamp: '2025-07-18T12:56:12.000Z',
      severity: 'medium',
      description: 'Login from new geographic location',
      blocked: false
    },
    {
      id: 3,
      type: 'Rate Limit Exceeded',
      user: 'api-client',
      ipAddress: '12.34.56.78',
      timestamp: '2025-07-18T11:41:12.000Z',
      severity: 'low',
      description: 'API rate limit exceeded',
      blocked: true
    }
  ]);

  const tabs = [
    {
      id: 'access-control',
      name: 'Access Control',
      icon: Lock,
      description: 'IP whitelisting and access restrictions'
    },
    {
      id: 'sessions',
      name: 'Active Sessions',
      icon: Users,
      description: 'Manage user sessions and devices'
    },
    {
      id: 'threat-detection',
      name: 'Threat Detection',
      icon: Shield,
      description: 'Security monitoring and incident logs'
    }
  ];

  const handleAddIp = async () => {
    if (newIp.ip && newIp.description) {
      try {
        const response = await ipWhitelistAPI.addIP({
          ip: newIp.ip,
          description: newIp.description,
          enabled: newIp.enabled
        });
        
        if (response.data.success) {
          setIpWhitelist(prev => [response.data.data, ...prev]);
          setNewIp({ ip: '', description: '', enabled: true });
          setHasChanges(true);
          alert('IP address added successfully!');
        }
      } catch (error) {
        console.error('Failed to add IP:', error);
        alert(error.response?.data?.message || 'Failed to add IP address');
      }
    }
  };

  const handleRemoveIp = async (id) => {
    try {
      const response = await ipWhitelistAPI.deleteIP(id);
      if (response.data.success) {
        setIpWhitelist(prev => prev.filter(item => item._id !== id));
        setHasChanges(true);
        alert('IP address removed successfully!');
      }
    } catch (error) {
      console.error('Failed to remove IP:', error);
      alert(error.response?.data?.message || 'Failed to remove IP address');
    }
  };

  const toggleIp = async (id) => {
    try {
      const response = await ipWhitelistAPI.toggleIP(id);
      if (response.data.success) {
        setIpWhitelist(prev => prev.map(item => 
          item._id === id ? response.data.data : item
        ));
        setHasChanges(true);
        alert(`IP address ${response.data.data.enabled ? 'enabled' : 'disabled'} successfully!`);
      }
    } catch (error) {
      console.error('Failed to toggle IP:', error);
      alert(error.response?.data?.message || 'Failed to toggle IP status');
    }
  };



  const handleMfaSettingsChange = (field, value) => {
    setMfaSettings(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setHasChanges(false);
      // Show success notification
    } catch (error) {
      console.error('Failed to save security settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTerminateSession = (sessionId) => {
    // Simulate session termination
    const sessionToTerminate = activeSessions.find(s => s.id === sessionId);
    if (sessionToTerminate) {
      setActiveSessions(prev => prev.filter(s => s.id !== sessionId));
      
      // In real implementation, this would call an API
      console.log(`Terminated session for user: ${sessionToTerminate.user}`);
      
      // Show confirmation (in real app, this would be a toast notification)
      alert(`Session terminated successfully for ${sessionToTerminate.displayName}`);
    }
  };

  const handleViewSession = (session) => {
    setSelectedSession(session);
    setShowSessionModal(true);
  };

  const renderAccessControlTab = () => (
    <div className="space-y-6">
      {/* Add New IP */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Plus className="h-5 w-5 mr-2" />
            Add IP Address
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                IP Address/CIDR
              </label>
              <input
                type="text"
                placeholder="192.168.1.1 or 192.168.1.0/24"
                value={newIp.ip}
                onChange={(e) => setNewIp(prev => ({ ...prev, ip: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <input
                type="text"
                placeholder="Office Network"
                value={newIp.description}
                onChange={(e) => setNewIp(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleAddIp}
                className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                Add IP
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* IP Whitelist */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Wifi className="h-5 w-5 mr-2" />
            IP Whitelist ({ipWhitelist.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">
                  IP Address
                </th>
                <th className="table-header-cell">
                  Description
                </th>
                <th className="table-header-cell">
                  Status
                </th>
                <th className="table-header-cell">
                  Created
                </th>
                <th className="table-header-cell">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ipWhitelist.map((item) => (
                <tr key={item._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      {item.ip}
                    </code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.enabled 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {item.enabled ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                      {item.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(item.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-start space-x-2">
                      <button
                        onClick={() => toggleIp(item._id)}
                        className="btn-primary btn-sm"
                      >
                        {item.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => handleRemoveIp(item._id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );



  const renderMfaTab = () => (
    <div className="space-y-6">
      {/* MFA Enforcement */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Smartphone className="h-5 w-5 mr-2" />
            MFA Enforcement
          </h3>
        </div>
        <div className="p-6 space-y-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Enforce MFA for All Users
                </label>
                <p className="text-xs text-gray-500">
                  Require all users to use multi-factor authentication
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={mfaSettings.enforcedForAll}
                  onChange={(e) => handleMfaSettingsChange('enforcedForAll', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Enforce MFA for Administrators
                </label>
                <p className="text-xs text-gray-500">
                  Require admin users to use multi-factor authentication
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={mfaSettings.enforcedForAdmins}
                  onChange={(e) => handleMfaSettingsChange('enforcedForAdmins', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Allow Backup Codes
                </label>
                <p className="text-xs text-gray-500">
                  Enable backup codes for account recovery
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={mfaSettings.backupCodes}
                  onChange={(e) => handleMfaSettingsChange('backupCodes', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              MFA Session Timeout (minutes)
            </label>
            <input
              type="number"
              min="15"
              max="480"
              value={mfaSettings.sessionTimeout}
              onChange={(e) => handleMfaSettingsChange('sessionTimeout', parseInt(e.target.value))}
              className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>
      </div>

      {/* MFA Methods */}
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Allowed MFA Methods</h3>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            {[
              { key: 'authenticator', label: 'Authenticator App (TOTP)', description: 'Google Authenticator, Authy, etc.' },
              { key: 'sms', label: 'SMS Text Message', description: 'Send codes via SMS' },
              { key: 'email', label: 'Email Code', description: 'Send codes via email' }
            ].map(({ key, label, description }) => (
              <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-700">{label}</label>
                  <p className="text-xs text-gray-500">{description}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mfaSettings.allowedMethods.includes(key)}
                    onChange={(e) => {
                      const methods = e.target.checked 
                        ? [...mfaSettings.allowedMethods, key]
                        : mfaSettings.allowedMethods.filter(m => m !== key);
                      handleMfaSettingsChange('allowedMethods', methods);
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderSessionsTab = () => (
    <div className="space-y-6">
      {/* Session Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-lg bg-green-100">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-secondary-600 truncate">
                  Total Active Sessions
                </p>
                <p className="text-2xl font-bold">
                  {activeSessions.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-lg bg-blue-100">
                <Activity className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-secondary-600 truncate">
                  Active Users
                </p>
                <p className="text-2xl font-bold">
                  {activeSessions.filter(s => s.status === 'active').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 rounded-lg bg-yellow-100">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-secondary-600 truncate">
                  Idle Sessions
                </p>
                <p className="text-2xl font-bold">
                  {activeSessions.filter(s => s.status === 'idle').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sessions Table */}
      <div className="bg-white rounded-lg border-light-gray  border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Demo Account Sessions ({activeSessions.length})
            </h3>
            <div className="text-sm text-gray-500">
              Real-time session monitoring for demo accounts
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">
                  User Account
                </th>
                <th className="table-header-cell">
                  Display Name
                </th>
                <th className="table-header-cell">
                  Location & IP
                </th>
                <th className="table-header-cell">
                  Device & Browser
                </th>
                <th className="table-header-cell">
                  Session Info
                </th>
                <th className="table-header-cell">
                  Status
                </th>
                <th className="table-header-cell">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activeSessions.map((session) => (
                <tr key={session.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{typeof session.user === 'string' ? session.user : 'Unknown User'}</div>
                      <div className="text-sm text-gray-500">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          session.role === 'Super Admin' ? 'bg-purple-100 text-purple-800' :
                          session.role === 'Facility Agent' ? 'bg-blue-100 text-blue-800' :
                          session.role === 'Issuer' ? 'bg-green-100 text-green-800' :
                          session.role === 'Custodian' ? 'bg-orange-100 text-orange-800' :
                          session.role === 'Arranger' ? 'bg-indigo-100 text-indigo-800' :
                          session.role === 'Exchange' ? 'bg-pink-100 text-pink-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {session.role}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{session.displayName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Globe className="h-4 w-4 text-gray-400 mr-2" />
                      <div>
                        <div className="text-sm text-gray-900">{session.location}</div>
                        <div className="text-sm text-gray-500 font-mono">{session.ipAddress}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {session.device}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">Login: {formatDate(session.loginTime)}</div>
                    <div className="text-sm text-gray-500">Last Activity: {formatDate(session.lastActivity)}</div>
                    <div className="text-sm text-gray-500">Duration: {session.sessionDuration}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      session.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      <Activity className="w-3 h-3 mr-1" />
                      {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button 
                        onClick={() => handleViewSession(session)}
                        className="text-primary-600 hover:text-gray-700"
                        title="View Session Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleTerminateSession(session.id)}
                        className="btn-primary btn-sm"
                        title="Terminate Session"
                      >
                        Terminate
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderThreatDetectionTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border-light-gray border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Shield className="h-5 w-5 mr-2" />
            Security Events ({securityEvents.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">
                  Event Type
                </th>
                <th className="table-header-cell">
                  User/Source
                </th>
                <th className="table-header-cell">
                  IP Address
                </th>
                <th className="table-header-cell">
                  Timestamp
                </th>
                <th className="table-header-cell">
                  Severity
                </th>
                <th className="table-header-cell">
                  Action Taken
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {securityEvents.map((event) => (
                <tr key={event.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{event.type}</div>
                    <div className="text-sm text-gray-500">{event.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {typeof event.user === 'string' ? event.user : 'Unknown User'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      {event.ipAddress}
                    </code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(event.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      event.severity === 'high' ? 'bg-red-100 text-red-800' :
                      event.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {event.severity.charAt(0).toUpperCase() + event.severity.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      event.blocked 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {event.blocked ? 'Blocked' : 'Allowed'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Page access is controlled by route permissions - no action-level check needed
  if (false) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Security Management</h1>
              <p className="text-sm text-gray-500 mt-1">Manage security policies, access controls, and threat detection</p>
            </div>
            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{saving ? 'Saving...' : ' Save Changes'}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      {/* Security Tabs */}
      <div className="  overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'access-control' && renderAccessControlTab()}
          {/* {activeTab === 'mfa-settings' && renderMfaTab()} */}
          {activeTab === 'sessions' && renderSessionsTab()}
          {activeTab === 'threat-detection' && renderThreatDetectionTab()}
        </div>
      </div>

      {/* Warning Banner */}
      {hasChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-400 mr-3 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Unsaved Changes</h3>
              <p className="text-sm text-yellow-700 mt-1">
                You have unsaved security configuration changes. Don't forget to save your changes.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Session Details Modal */}
      {showSessionModal && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 pb-0">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Activity className="h-5 w-5 mr-2" />
                  Session Details - {selectedSession.displayName}
                </h3>
                <button
                  onClick={() => setShowSessionModal(false)}
                  className="text-gray-400 hover:text-gray-600 h-6 w-6"
                >
                  <X className="h-6 w-6" style={{ color: '#9ca3af' }} />
                </button>
              </div>
            </div>
            <div className="border-b border-light-gray"></div>
            <div className="p-6 space-y-6">
              {/* User Information */}
              <div className="bg-gray-50 rounded-lg">
                <div className="p-4 pb-0">
                  <h4 className="font-medium text-gray-900 mb-3">User Information</h4>
                </div>
                <div className="border-b border-light-gray"></div>
                <div className="p-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Email:</span>
                                            <div className="font-medium">{typeof selectedSession.user === 'string' ? selectedSession.user : 'Unknown User'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Display Name:</span>
                    <div className="font-medium">{selectedSession.displayName}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Role:</span>
                    <div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        selectedSession.role === 'Super Admin' ? 'bg-purple-100 text-purple-800' :
                        selectedSession.role === 'Facility Agent' ? 'bg-blue-100 text-blue-800' :
                        selectedSession.role === 'Issuer' ? 'bg-green-100 text-green-800' :
                        selectedSession.role === 'Custodian' ? 'bg-orange-100 text-orange-800' :
                        selectedSession.role === 'Arranger' ? 'bg-indigo-100 text-indigo-800' :
                        selectedSession.role === 'Exchange' ? 'bg-pink-100 text-pink-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedSession.role}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Status:</span>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        selectedSession.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        <Activity className="w-3 h-3 mr-1" />
                        {selectedSession.status.charAt(0).toUpperCase() + selectedSession.status.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Session Information */}
              <div className="bg-blue-50 rounded-lg">
                <div className="p-4 pb-0">
                  <h4 className="font-medium text-gray-900 mb-3">Session Information</h4>
                </div>
                <div className="border-b border-light-gray"></div>
                <div className="p-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Login Time:</span>
                    <div className="font-medium">{formatDate(selectedSession.loginTime)}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Last Activity:</span>
                    <div className="font-medium">{formatDate(selectedSession.lastActivity)}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Session Duration:</span>
                    <div className="font-medium">{selectedSession.sessionDuration}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Device & Browser:</span>
                    <div className="font-medium">{selectedSession.device}</div>
                  </div>
                </div>
              </div>

              {/* Location & Network Information */}
              <div className="bg-green-50 rounded-lg">
                <div className="p-4 pb-0">
                  <h4 className="font-medium text-gray-900 mb-3">Location & Network</h4>
                </div>
                <div className="border-b border-light-gray"></div>
                <div className="p-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">IP Address:</span>
                    <div className="font-mono font-medium">{selectedSession.ipAddress}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Location:</span>
                    <div className="font-medium">{selectedSession.location}</div>
                  </div>
                </div>
              </div>

              {/* Security Information */}
              <div className="bg-yellow-50 rounded-lg">
                <div className="p-4 pb-0">
                  <h4 className="font-medium text-gray-900 mb-3">Security Information</h4>
                </div>
                <div className="border-b border-light-gray"></div>
                <div className="p-4 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Session ID:</span>
                    <div className="font-mono text-xs">{`sess_${selectedSession.id}_${Date.now().toString(36)}`}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Login Method:</span>
                    <div className="font-medium">Email + Password</div>
                  </div>
                  <div>
                    <span className="text-gray-500">MFA Status:</span>
                    <div className="font-medium text-green-600">✓ Enabled</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Risk Level:</span>
                    <div className="font-medium text-green-600">Low</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3  px-6 mb-6">
              <button
                onClick={() => setShowSessionModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleTerminateSession(selectedSession.id);
                  setShowSessionModal(false);
                }}
                className="btn-primary"
              >
                Terminate Session
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default SecurityManagement; 